// openEHR BASE model — BMM package org.openehr.base.foundation_types.time
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { TYPE_REGISTRY } from "../_shared.ts";
import type { Any } from "./foundation_types.ts";
import { Boolean, Integer, Ordered, String } from "./primitive_types.ts";
import { builtinTemporal } from "./temporal_api.ts";

// The class in this file is also named Temporal, so the TC39 global is aliased.
// Deno 2.7+ provides this global with no flag (https://deno.com/blog/v2.7).
const TemporalAPI = builtinTemporal();

/**
 * Abstract ancestor of time-related classes.
 */
export abstract class Temporal extends Ordered {
}

/**
 * Definitions for date/time classes. Note that the timezone limits are set by where the international dateline is. Thus, time in New Zealand is quoted using \`+12:00\`, not \`-12:00\`.
 */
export class Time_Definitions {
  /**
   * True if \`y >= 0\`.
   *
   * TODO: Verify if this validation is sufficient. The openEHR specification
   * states y >= 0 is correct (no negative years, only 4-digit years assumed),
   * but this may need additional validation for:
   * - Maximum year value (e.g., 9999 for 4-digit constraint)
   * - Whether year 0 is historically/calendrically valid
   * See corresponding TODO in tasks/instructions/base/Time_Definitions.md
   *
   * @param y - Parameter
   * @returns Result value
   */
  valid_year(y: Integer): Boolean {
    const val = y.value || 0;
    return new Boolean(val >= 0);
  }

  /**
   * True if \`m >= 1 and m <= months_in_year\`.
   * @param m - Parameter
   * @returns Result value
   */
  valid_month(m: Integer): Boolean {
    const val = m.value || 0;
    return new Boolean(val >= 1 && val <= 12);
  }

  /**
   * True if \`d >= 1 and d <= days_in_month (m, y)\`.
   * @param y - Parameter
   * @param m - Parameter
   * @param d - Parameter
   * @returns Result value
   */
  valid_day(y: Integer, m: Integer, d: Integer): Boolean {
    const dVal = d.value || 0;
    const mVal = m.value || 1;
    const yVal = y.value || 0;

    if (dVal < 1) return new Boolean(false);

    // Days in each month
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Check for leap year
    const isLeapYear = (yVal % 4 === 0 && yVal % 100 !== 0) ||
      (yVal % 400 === 0);
    if (isLeapYear && mVal === 2) {
      return new Boolean(dVal <= 29);
    }

    return new Boolean(dVal <= (daysInMonth[mVal - 1] || 31));
  }

  /**
   * True if \`(h >= 0 and h < Hours_in_day) or (h = Hours_in_day and m = 0 and s = 0)\` .
   * @param h - Parameter
   * @param m - Parameter
   * @param s - Parameter
   * @returns Result value
   */
  valid_hour(h: Integer, m: Integer, s: Integer): Boolean {
    const hVal = h.value || 0;
    const mVal = m.value || 0;
    const sVal = s.value || 0;

    // Normal hours: 0-23
    if (hVal >= 0 && hVal < 24) return new Boolean(true);

    // Special case: 24:00:00 is valid
    if (hVal === 24 && mVal === 0 && sVal === 0) return new Boolean(true);

    return new Boolean(false);
  }

  /**
   * True if \`m >= 0 and m < Minutes_in_hour\`.
   * @param m - Parameter
   * @returns Result value
   */
  valid_minute(m: Integer): Boolean {
    const val = m.value || 0;
    return new Boolean(val >= 0 && val < 60);
  }

  /**
   * True if \`s >= 0 and s < Seconds_in_minute\` .
   * @param s - Parameter
   * @returns Result value
   */
  valid_second(s: Integer): Boolean {
    const val = s.value || 0;
    return new Boolean(val >= 0 && val < 60);
  }

  /**
   * True if \`fs >= 0.0\` and \`fs < 1.0\` .
   * @param fs - Parameter
   * @returns Result value
   */
  valid_fractional_second(fs: number): Boolean {
    return new Boolean(fs >= 0.0 && fs < 1.0);
  }

  /**
   * String is a valid ISO 8601 date, i.e. takes the complete form:
   *
   * * \`YYYY-MM-DD\` (extended, preferred) or one of the partial forms \`YYYY-MM\` or \`YYYY\`
   * * \`YYYYMMDD\` (compact) or a partial variant \`YYYYMM\`.
   *
   * Where:
   *
   * * \`YYYY\` is the string form of any positive number in the range \`0000\` - \`9999\` (zero-filled to four digits)
   * * \`MM\` is \`01\` - \`12\` (zero-filled to two digits)
   * * \`DD\` is \`01\` - \`31\` (zero-filled to two digits)
   *
   * The combinations of \`YYYY\`, \`MM\`, \`DD\` numbers must be correct with respect to the Gregorian calendar.
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_date(s: String): Boolean {
    const val = s.value || "";

    // Extended format: YYYY-MM-DD, YYYY-MM, or YYYY
    const extendedPattern =
      /^(\d{4})(-((0[1-9]|1[0-2])(-([0-2]\d|3[01]))?)?)?$/;

    // Compact format: YYYYMMDD, YYYYMM, or YYYY
    const compactPattern = /^(\d{4})((0[1-9]|1[0-2])([0-2]\d|3[01])?)?$/;

    return new Boolean(
      extendedPattern.test(val) || compactPattern.test(val),
    );
  }

  /**
   * String is a valid ISO 8601 date, i.e. takes the form:
   *
   * * \`hh:mm:ss[(,|.)s+][Z|±hh[:mm]]\` (extended)
   * * \`hhmmss[(,|.)s+][Z|±hh[mm]]\` (compact)
   * * or one of the partial forms:
   * ** \`hh:mm\` (extended)
   * ** \`hhmm\` or \`hh\` (compact)
   *
   * with an additional optional timezone indicator of:
   *
   * * \`Z\` or \`±hh[:mm]\` (extended)  \`±hh[mm]\` (compact)
   *
   * Where:
   *
   * * \`hh\` is "00" - "23" (0-filled to two digits)
   * * \`mm\` is "00" - "59" (0-filled to two digits)
   * * \`ss\` is "00" - "60" (0-filled to two digits)
   * * \`[(,|.)s+]\` is an optional string consisting of a comma or decimal point followed by numeric string of 1 or more digits, representing a fractional second
   * * \`Z\` is a literal meaning UTC (modern replacement for GMT), i.e. timezone \`+0000\`
   *
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_time(s: String): Boolean {
    const val = s.value || "";

    // Pattern for ISO 8601 time with optional fractional seconds and timezone
    const timePattern =
      /^([01]\d|2[0-3]):?([0-5]\d)?:?([0-5]\d|60)?([.,]\d+)?(Z|[+-]([01]\d|2[0-3]):?([0-5]\d)?)?$/;

    return new Boolean(timePattern.test(val));
  }

  /**
   * String is a valid ISO 8601 date-time, i.e. takes the form:
   *
   * * \`YYYY-MM-DDThh:mm:ss[(,|.)s+][Z|±hh[:mm]]\` (extended)
   * * \`YYYYMMDDThhmmss[(,|.)s+][Z|±hh[mm]]\` (compact)
   * * or one of the partial forms:
   * ** \`YYYY-MM-DDThh:mm\` or \`YYYY-MM-DDThh\` (extended)
   * ** \`YYYYMMDDThhmm\` or \`YYYYMMDDThh\` (compact)
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_date_time(s: String): Boolean {
    const val = s.value || "";

    // Pattern for ISO 8601 date-time
    const dateTimePattern =
      /^(\d{4})-?(0[1-9]|1[0-2])-?([0-2]\d|3[01])T([01]\d|2[0-3]):?([0-5]\d)?:?([0-5]\d|60)?([.,]\d+)?(Z|[+-]([01]\d|2[0-3]):?([0-5]\d)?)?$/;

    return new Boolean(dateTimePattern.test(val));
  }

  /**
   * String is a valid ISO 8601 duration, i.e. takes the form:
   *
   * * \`P[nnY][nnM][nnW][nnD][T[nnH][nnM][nnS]]\`
   *
   * Where each nn represents a number of years, months, etc. \`nnW\` represents a number of 7-day weeks.
   *
   * Note: allowing the \`W\` designator in the same expression as other designators is an exception to the published standard, but necessary in clinical information (typically for representing pregnancy duration).
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_duration(s: String): Boolean {
    const val = s.value || "";

    // Pattern for ISO 8601 duration: P[nY][nM][nW][nD][T[nH][nM][nS]]
    const durationPattern =
      /^P(?:\d+Y)?(?:\d+M)?(?:\d+W)?(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?(?:\d+(?:\.\d+)?S)?)?$/;

    // Must start with P and have at least one component
    return new Boolean(durationPattern.test(val) && val.length > 1);
  }
}

/**
 * Abstract ancestor type of ISO 8601 types, defining interface for 'extended' and 'partial' concepts from ISO 8601.
 */
export abstract class Iso8601_type extends Temporal {
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: String;

  /**
   * Representation of all descendants is a single String.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the String wrapper object for value.
   * Use this to access String methods.
   */
  get $value(): String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or String wrapper.
   */
  set value(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * True if this date time is partial, i.e. if trailing end (right hand) value(s) is/are missing.
   * @returns Result value
   */
  abstract is_partial(): Boolean;

  /**
   * True if this ISO8601 string is in the 'extended' form, i.e. uses \`'-'\` and / or \`':'\` separators. This is the preferred format.
   * @returns Result value
   */
  abstract is_extended(): Boolean;
}

/**
 * Represents an ISO 8601 date/time, including partial and extended forms. Value may be:
 *
 * * \`YYYY-MM-DDThh:mm:ss[(,|.)sss][Z | ±hh[:mm]]\` (extended, preferred) or
 * * \`YYYYMMDDThhmmss[(,|.)sss][Z | ±hh[mm]]\` (compact)
 * * or a partial variant.
 *
 * See \`_valid_iso8601_date_time()_\` for validity.
 *
 * Note that this class includes 2 deviations from ISO 8601:2004:
 *
 * * for partial date/times, any part of the date/time up to the month may be missing, not just seconds and minutes as in the standard;
 * * the time \`24:00:00\` is not allowed, since it would mean the date was really on the next day.
 */
export class Iso8601_date_time extends Iso8601_type {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("ISO8601_DATE_TIME", Iso8601_date_time);
  }

  /**
   * Extract the year part of the date as an Integer.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  year(): Integer {
    const val = this.value || "";
    try {
      // Try full date-time first
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.year);
    } catch {
      try {
        // Try just date part if full parse fails
        const match = val.match(/^(\d{4})-?(\d{2})?-?(\d{2})?/);
        if (match && match[1]) {
          return Integer.from(parseInt(match[1], 10));
        }
      } catch {
        // Fall through
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the month part of the date/time as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  month(): Integer {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.month);
    } catch {
      try {
        const match = val.match(/^(\d{4})-?(\d{2})?/);
        if (match && match[2]) {
          return Integer.from(parseInt(match[2], 10));
        }
      } catch {
        // Fall through
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the day part of the date/time as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  day(): Integer {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.day);
    } catch {
      try {
        const match = val.match(/^(\d{4})-?(\d{2})?-?(\d{2})?/);
        if (match && match[3]) {
          return Integer.from(parseInt(match[3], 10));
        }
      } catch {
        // Fall through
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the hour part of the date/time as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  hour(): Integer {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.hour);
    } catch {
      // Return 0 if parsing fails or no time component
    }
    return Integer.from(0);
  }

  /**
   * Extract the minute part of the date/time as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  minute(): Integer {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.minute);
    } catch {
      // Return 0 if parsing fails or no time component
    }
    return Integer.from(0);
  }

  /**
   * Extract the integral seconds part of the date/time (i.e. prior to any decimal sign) as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  second(): Integer {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      return Integer.from(dt.second);
    } catch {
      // Return 0 if parsing fails or no time component
    }
    return Integer.from(0);
  }

  /**
   * Extract the fractional seconds part of the date/time (i.e. following to any decimal sign) as a Real, or return 0.0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 parsing.
   * @returns Result value
   */
  fractional_second(): number {
    const val = this.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      // Temporal stores subsecond precision in millisecond, microsecond, and nanosecond fields
      // Convert to fractional seconds: milliseconds / 1000
      return dt.millisecond / 1000 + dt.microsecond / 1000000 +
        dt.nanosecond / 1000000000;
    } catch {
      // Return 0.0 if parsing fails
    }
    return 0.0;
  }

  /**
   * Timezone; may be Void.
   *
   * Uses Temporal API to extract timezone information.
   * @returns Result value
   */
  timezone(): Iso8601_timezone {
    const val = this.value || "";
    // Match timezone: Z or ±hh:mm or ±hhmm
    const match = val.match(/(Z|[+-]\d{2}:?\d{2})$/);
    if (match) {
      const tz = new Iso8601_timezone();
      tz.value = match[1];
      return tz;
    }
    throw new Error("No timezone present in date-time");
  }

  /**
   * Indicates whether month in year is unknown.
   * @returns Result value
   */
  month_unknown(): Boolean {
    return new Boolean(this.month().value === 0);
  }

  /**
   * Indicates whether day in month is unknown.
   * @returns Result value
   */
  day_unknown(): Boolean {
    return new Boolean(this.day().value === 0);
  }

  /**
   * Indicates whether minute in hour is known.
   * @returns Result value
   */
  minute_unknown(): Boolean {
    const val = this.value || "";
    // Check if time part exists with minutes
    return new Boolean(!val.includes("T") || this.minute().value === 0);
  }

  /**
   * Indicates whether minute in hour is known.
   * @returns Result value
   */
  second_unknown(): Boolean {
    const val = this.value || "";
    // Check if seconds are present in the string
    const hasSeconds = /T\d{2}:?\d{2}:?\d{2}/.test(val);
    return new Boolean(!hasSeconds);
  }

  /**
   * True if this time has a decimal part indicated by \`','\` (comma) rather than \`'.'\` (period).
   * @returns Result value
   */
  is_decimal_sign_comma(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes(","));
  }

  /**
   * True if this date time is partial, i.e. if seconds or more is missing.
   * @returns Result value
   */
  is_partial(): Boolean {
    return this.second_unknown();
  }

  /**
   * True if this date/time uses \`'-'\`, \`':'\` separators.
   * @returns Result value
   */
  is_extended(): Boolean {
    const val = this.value || "";
    // Extended format uses '-' in date part and ':' in time part
    return new Boolean(val.includes("-") || val.includes(":"));
  }

  /**
   * True if the \`_fractional_second_\` part is significant (i.e. even if = 0.0).
   * @returns Result value
   */
  has_fractional_second(): Boolean {
    const val = this.value || "";
    // Check for comma or period after seconds
    return new Boolean(/T\d{2}:?\d{2}:?\d{2}[,.]/.test(val));
  }

  /**
   * Return the string value in extended format.
   *
   * Uses Temporal API to parse and format in extended ISO 8601 format.
   * @returns Result value
   */
  as_string(): String {
    const val = this.value || "";
    try {
      // Parse with Temporal and convert to extended format string
      const dt = TemporalAPI.PlainDateTime.from(val);
      return String.from(dt.toString());
    } catch {
      // If parsing fails, return original value
      return String.from(val);
    }
  }

  /**
   * Arithmetic addition of a duration to a date/time.
   * @param a_diff - Parameter
   * @returns Result value
   */
  add(a_diff: Iso8601_duration): Iso8601_date_time {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = dt.add(dur);
      const newDateTime = new Iso8601_date_time();
      newDateTime.value = result.toString();
      return newDateTime;
    } catch (e) {
      throw new Error(`Failed to add duration to date_time: ${e}`);
    }
  }

  /**
   * Arithmetic subtraction of a duration from a date/time.
   * @param a_diff - Parameter
   * @returns Result value
   */
  subtract(a_diff: Iso8601_duration): Iso8601_date_time {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = dt.subtract(dur);
      const newDateTime = new Iso8601_date_time();
      newDateTime.value = result.toString();
      return newDateTime;
    } catch (e) {
      throw new Error(`Failed to subtract duration from date_time: ${e}`);
    }
  }

  /**
   * Difference of two date/times.
   * @param a_date_time - Parameter
   * @returns Result value
   */
  diff(a_date_time: Iso8601_date_time): Iso8601_duration {
    const val = this.value || "";
    const otherVal = a_date_time.value || "";
    try {
      const dt1 = TemporalAPI.PlainDateTime.from(val);
      const dt2 = TemporalAPI.PlainDateTime.from(otherVal);
      const diff = dt1.since(dt2);
      const duration = new Iso8601_duration();
      duration.value = diff.toString();
      return duration;
    } catch (e) {
      throw new Error(`Failed to calculate difference: ${e}`);
    }
  }

  /**
   * Addition of nominal duration represented by \`_a_diff_\`. See \`Iso8601_date._add_nominal_()\` for semantics.
   * @param a_diff - Parameter
   * @returns Result value
   */
  add_nominal(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = dt.add(dur);
      const newDateTime = new Iso8601_date();
      newDateTime.value = result.toPlainDate().toString();
      return newDateTime;
    } catch (e) {
      throw new Error(`Failed to add nominal duration: ${e}`);
    }
  }

  /**
   * Subtraction of nominal duration represented by \`_a_diff_\`. See \`_add_nominal_()\` for semantics.
   * @param a_diff - Parameter
   * @returns Result value
   */
  subtract_nominal(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const dt = TemporalAPI.PlainDateTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = dt.subtract(dur);
      const newDateTime = new Iso8601_date();
      newDateTime.value = result.toPlainDate().toString();
      return newDateTime;
    } catch (e) {
      throw new Error(`Failed to subtract nominal duration: ${e}`);
    }
  }

  /**
   * Compares this date-time with another for ordering.
   * @param other - The object to compare with
   * @returns true if this date-time is less than the other
   */
  override less_than(other: Ordered): Boolean {
    if (!(other instanceof Iso8601_date_time)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const dt1 = TemporalAPI.PlainDateTime.from(val);
      const dt2 = TemporalAPI.PlainDateTime.from(otherVal);
      return new Boolean(TemporalAPI.PlainDateTime.compare(dt1, dt2) < 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val < otherVal);
    }
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Iso8601_date_time)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const dt1 = TemporalAPI.PlainDateTime.from(val);
      const dt2 = TemporalAPI.PlainDateTime.from(otherVal);
      return new Boolean(TemporalAPI.PlainDateTime.compare(dt1, dt2) === 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val === otherVal);
    }
  }
}

/**
 * Represents an ISO 8601 duration, which may have multiple parts from years down to seconds. The \`_value_\` attribute is a String in the format:
 *
 * * \`P[nnY][nnM][nnW][nnD][T[nnH][nnM][nnS]]\`
 *
 * NOTE: two deviations from ISO 8601 are supported, the first, to allow a negative sign, and the second allowing the 'W' designator to be mixed with other designators.
 */
export class Iso8601_duration extends Iso8601_type {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("ISO8601_DURATION", Iso8601_duration);
  }

  /**
   * Helper method to convert openEHR duration with weeks to standard ISO 8601.
   * OpenEHR allows weeks to be mixed with other designators, but Temporal API doesn't.
   * This converts weeks to days (1W = 7D).
   * @param value - Duration string that may contain weeks
   * @returns Duration string with weeks converted to days
   */
  static normalizeWeeks(value: string): string {
    // Match weeks designator: nW or nnnW
    const weeksMatch = value.match(/(\d+(?:\.\d+)?)W/);
    if (!weeksMatch) return value;

    const weeks = parseFloat(weeksMatch[1]);
    const days = weeks * 7;

    // Remove the weeks component
    let normalized = value.replace(/\d+(?:\.\d+)?W/, "");

    // Add days to existing days or insert days
    const daysMatch = normalized.match(/(\d+(?:\.\d+)?)D/);
    if (daysMatch) {
      const existingDays = parseFloat(daysMatch[1]);
      const totalDays = existingDays + days;
      normalized = normalized.replace(/\d+(?:\.\d+)?D/, `${totalDays}D`);
    } else {
      // Insert days before T (time) or at end if no time component
      if (normalized.includes("T")) {
        normalized = normalized.replace("T", `${days}DT`);
      } else {
        // Add days before the end
        normalized = normalized.replace(/P(.*)$/, `P$1${days}D`);
      }
    }

    return normalized;
  }

  /**
   * Returns True.
   * @returns Result value
   */
  is_extended(): Boolean {
    return new Boolean(true);
  }

  /**
   * Returns False.
   * @returns Result value
   */
  is_partial(): Boolean {
    return new Boolean(false);
  }

  /**
   * Number of years in the \`_value_\`, i.e. the number preceding the \`'Y'\` in the \`'YMD'\` part, if one exists.
   * @returns Result value
   */
  years(): Integer {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return Integer.from(dur.years || 0);
    } catch {
      return Integer.from(0);
    }
  }

  /**
   * Number of months in the \`_value_\`, i.e. the value preceding the \`'M'\` in the \`'YMD'\` part, if one exists.
   * @returns Result value
   */
  months(): Integer {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return Integer.from(dur.months || 0);
    } catch {
      return Integer.from(0);
    }
  }

  /**
   * Number of days in the \`_value_\`, i.e. the number preceding the \`'D'\` in the \`'YMD'\` part, if one exists.
   * Note: This returns only the D component, not converted weeks.
   * @returns Result value
   */
  days(): Integer {
    const val = this.value || "";
    try {
      // Normalize weeks to days for parsing, then extract days
      const normalized = Iso8601_duration.normalizeWeeks(val);
      const dur = TemporalAPI.Duration.from(normalized);
      return Integer.from(dur.days || 0);
    } catch {
      // Fallback: try to extract days directly
      const daysMatch = val.match(/(\d+(?:\.\d+)?)D/);
      if (daysMatch) {
        return Integer.from(Math.floor(parseFloat(daysMatch[1])));
      }
      return Integer.from(0);
    }
  }

  /**
   * Number of hours in the \`_value_\`, i.e. the number preceding the \`'H'\` in the \`'HMS'\` part, if one exists.
   * @returns Result value
   */
  hours(): Integer {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return Integer.from(dur.hours || 0);
    } catch {
      return Integer.from(0);
    }
  }

  /**
   * Number of minutes in the \`_value_\`, i.e. the number preceding the \`'M'\` in the \`'HMS'\` part, if one exists.
   * @returns Result value
   */
  minutes(): Integer {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return Integer.from(dur.minutes || 0);
    } catch {
      return Integer.from(0);
    }
  }

  /**
   * Number of seconds in the \`_value_\`, i.e. the integer number preceding the \`'S'\` in the \`'HMS'\` part, if one exists.
   * @returns Result value
   */
  seconds(): Integer {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return Integer.from(dur.seconds || 0);
    } catch {
      return Integer.from(0);
    }
  }

  /**
   * Fractional seconds in the \`_value_\`, i.e. the decimal part of the number preceding the \`'S'\` in the \`'HMS'\` part, if one exists.
   * @returns Result value
   */
  fractional_seconds(): number {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      return (dur.milliseconds || 0) / 1000 +
        (dur.microseconds || 0) / 1000000 + (dur.nanoseconds || 0) / 1000000000;
    } catch {
      return 0.0;
    }
  }

  /**
   * Number of weeks in the \`_value_\`, i.e. the value preceding the \`W\`, if one exists.
   * @returns Result value
   */
  weeks(): Integer {
    const val = this.value || "";
    // Extract weeks directly from the string since openEHR allows mixed W with other designators
    const weeksMatch = val.match(/(\d+(?:\.\d+)?)W/);
    if (weeksMatch) {
      return Integer.from(Math.floor(parseFloat(weeksMatch[1])));
    }
    return Integer.from(0);
  }

  /**
   * True if this time has a decimal part indicated by ',' (comma) rather than '.' (period).
   * @returns Result value
   */
  is_decimal_sign_comma(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes(","));
  }

  /**
   * Total number of seconds equivalent (including fractional) of entire duration. Where non-definite elements such as year and month (i.e. 'Y' and 'M') are included, the corresponding 'average' durations from \`Time_definitions\` are used to compute the result.
   * @returns Result value
   */
  to_seconds(): number {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(val);
      // Note: For year/month, we use approximate values (not calendar-aware)
      const totalSeconds = (dur.years || 0) * 31536000 +
        (dur.months || 0) * 2592000 +
        (dur.weeks || 0) * 604800 + (dur.days || 0) * 86400 +
        (dur.hours || 0) * 3600 + (dur.minutes || 0) * 60 + (dur.seconds || 0) +
        (dur.milliseconds || 0) / 1000 + (dur.microseconds || 0) / 1000000 +
        (dur.nanoseconds || 0) / 1000000000;
      return totalSeconds;
    } catch {
      return 0.0;
    }
  }

  /**
   * Return the duration string value.
   * @returns Result value
   */
  as_string(): String {
    const val = this.value || "";
    try {
      // Parse and normalize the duration string
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      return String.from(dur.toString());
    } catch {
      // If parsing fails, return original value
      return String.from(val);
    }
  }

  /**
   * Arithmetic addition of a duration to a duration, via conversion to seconds, using \`Time_definitions._Average_days_in_year_\` and \`Time_definitions._Average_days_in_month_\`
   * @param a_val - Parameter
   * @returns Result value
   */
  add(a_val: Iso8601_duration): Iso8601_duration {
    const val = this.value || "";
    const otherVal = a_val.value || "";
    try {
      const dur1 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      const dur2 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(otherVal),
      );
      const result = dur1.add(dur2);
      const newDuration = new Iso8601_duration();
      newDuration.value = result.toString();
      return newDuration;
    } catch (e) {
      throw new Error(`Failed to add durations: ${e}`);
    }
  }

  /**
   * Arithmetic subtraction of a duration from a duration, via conversion to seconds, using \`Time_definitions._Average_days_in_year_\` and \`Time_definitions._Average_days_in_month_\`
   * @param a_val - Parameter
   * @returns Result value
   */
  subtract(a_val: Iso8601_duration): Iso8601_duration {
    const val = this.value || "";
    const otherVal = a_val.value || "";
    try {
      const dur1 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      const dur2 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(otherVal),
      );
      const result = dur1.subtract(dur2);
      const newDuration = new Iso8601_duration();
      newDuration.value = result.toString();
      return newDuration;
    } catch (e) {
      throw new Error(`Failed to subtract durations: ${e}`);
    }
  }

  /**
   * Arithmetic multiplication a duration by a number.
   * @param a_val - Parameter
   * @returns Result value
   */
  multiply(a_val: number): Iso8601_duration {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      // Multiply each component of the duration
      const result = dur.add(dur.negated()).add({
        years: dur.years * a_val,
        months: dur.months * a_val,
        weeks: dur.weeks * a_val,
        days: dur.days * a_val,
        hours: dur.hours * a_val,
        minutes: dur.minutes * a_val,
        seconds: dur.seconds * a_val,
        milliseconds: dur.milliseconds * a_val,
        microseconds: dur.microseconds * a_val,
        nanoseconds: dur.nanoseconds * a_val,
      });
      const newDuration = new Iso8601_duration();
      newDuration.value = result.toString();
      return newDuration;
    } catch (e) {
      throw new Error(`Failed to multiply duration: ${e}`);
    }
  }

  /**
   * Arithmetic division of a duration by a number.
   * @param a_val - Parameter
   * @returns Result value
   */
  divide(a_val: number): Iso8601_duration {
    if (a_val === 0) {
      throw new Error("Division by zero");
    }
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      // Divide each component of the duration
      const result = dur.add(dur.negated()).add({
        years: dur.years / a_val,
        months: dur.months / a_val,
        weeks: dur.weeks / a_val,
        days: dur.days / a_val,
        hours: dur.hours / a_val,
        minutes: dur.minutes / a_val,
        seconds: dur.seconds / a_val,
        milliseconds: dur.milliseconds / a_val,
        microseconds: dur.microseconds / a_val,
        nanoseconds: dur.nanoseconds / a_val,
      });
      const newDuration = new Iso8601_duration();
      newDuration.value = result.toString();
      return newDuration;
    } catch (e) {
      throw new Error(`Failed to divide duration: ${e}`);
    }
  }

  /**
   * Generate negative of current duration value.
   * @returns Result value
   */
  negative(): Iso8601_duration {
    const val = this.value || "";
    try {
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      const result = dur.negated();
      const newDuration = new Iso8601_duration();
      newDuration.value = result.toString();
      return newDuration;
    } catch (e) {
      throw new Error(`Failed to negate duration: ${e}`);
    }
  }

  /**
   * Compares this duration with another for ordering.
   * @param other - The object to compare with
   * @returns true if this duration is less than the other
   */
  override less_than(other: Ordered): Boolean {
    if (!(other instanceof Iso8601_duration)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const dur1 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      const dur2 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(otherVal),
      );
      // Compare total seconds as a simple approximation
      const total1 = dur1.total({ unit: "seconds" });
      const total2 = dur2.total({ unit: "seconds" });
      return new Boolean(total1 < total2);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val < otherVal);
    }
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Iso8601_duration)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const dur1 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(val),
      );
      const dur2 = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(otherVal),
      );
      // Compare total seconds
      const total1 = dur1.total({ unit: "seconds" });
      const total2 = dur2.total({ unit: "seconds" });
      return new Boolean(total1 === total2);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val === otherVal);
    }
  }
}

/**
 * Represents an ISO 8601 time, including partial and extended forms. Value may be:
 *
 * * \`hh:mm:ss[(,|.)sss][Z|±hh[:mm]]\` (extended, preferred) or
 * * \`hhmmss[(,|.)sss][Z|±hh[mm]]\` (compact)
 * * or a partial invariant.
 *
 * See \`_valid_iso8601_time()_\` for validity.
 *
 * NOTE: A small deviation to the ISO 8601:2004 standard in this class is that the time \`24:00:00\` is not allowed, for consistency with \`Iso8601_date_time\`.
 */
export class Iso8601_time extends Iso8601_type {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("ISO8601_TIME", Iso8601_time);
  }

  /**
   * Extract the hour part of the date/time as an Integer.
   * @returns Result value
   */
  hour(): Integer {
    const val = this.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      return Integer.from(time.hour);
    } catch {
      // Try to parse just hour from partial time
      const match = val.match(/^(\d{2})/);
      if (match) {
        return Integer.from(parseInt(match[1], 10));
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the minute part of the time as an Integer, or return 0 if not present.
   * @returns Result value
   */
  minute(): Integer {
    const val = this.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      return Integer.from(time.minute);
    } catch {
      // Return 0 if parsing fails or no minute component
    }
    return Integer.from(0);
  }

  /**
   * Extract the integral seconds part of the time (i.e. prior to any decimal sign) as an Integer, or return 0 if not present.
   * @returns Result value
   */
  second(): Integer {
    const val = this.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      return Integer.from(time.second);
    } catch {
      // Return 0 if parsing fails or no second component
    }
    return Integer.from(0);
  }

  /**
   * Extract the fractional seconds part of the time (i.e. following to any decimal sign) as a Real, or return 0.0 if not present.
   * @returns Result value
   */
  fractional_second(): number {
    const val = this.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      // Temporal stores subsecond precision in millisecond, microsecond, and nanosecond fields
      return time.millisecond / 1000 + time.microsecond / 1000000 +
        time.nanosecond / 1000000000;
    } catch {
      // Return 0.0 if parsing fails
    }
    return 0.0;
  }

  /**
   * Timezone; may be Void.
   * @returns Result value
   */
  timezone(): Iso8601_timezone {
    const val = this.value || "";
    const match = val.match(/(Z|[+-]\d{2}:?\d{2})$/);
    if (match) {
      const tz = new Iso8601_timezone();
      tz.value = match[1];
      return tz;
    }
    throw new Error("No timezone present in time");
  }

  /**
   * Indicates whether minute is unknown. If so, the time is of the form “hh”.
   * @returns Result value
   */
  minute_unknown(): Boolean {
    const val = this.value || "";
    const hasMinutes = /^\d{2}:?\d{2}/.test(val);
    return new Boolean(!hasMinutes);
  }

  /**
   * Indicates whether second is unknown. If so and minute is known, the time is of the form \`"hh:mm"\` or \`"hhmm"\`.
   * @returns Result value
   */
  second_unknown(): Boolean {
    const val = this.value || "";
    const hasSeconds = /^\d{2}:?\d{2}:?\d{2}/.test(val);
    return new Boolean(!hasSeconds);
  }

  /**
   * True if this time has a decimal part indicated by \`','\` (comma) rather than \`'.'\` (period).
   * @returns Result value
   */
  is_decimal_sign_comma(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes(","));
  }

  /**
   * True if this time is partial, i.e. if seconds or more is missing.
   * @returns Result value
   */
  is_partial(): Boolean {
    return this.second_unknown();
  }

  /**
   * True if this time uses \`'-'\`, \`':'\` separators.
   * @returns Result value
   */
  is_extended(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes(":"));
  }

  /**
   * True if the \`_fractional_second_\` part is significant (i.e. even if = 0.0).
   * @returns Result value
   */
  has_fractional_second(): Boolean {
    const val = this.value || "";
    return new Boolean(/\d{2}[,.]/.test(val));
  }

  /**
   * Return string value in extended format.
   * @returns Result value
   */
  as_string(): String {
    const val = this.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      return String.from(time.toString());
    } catch {
      return String.from(val);
    }
  }

  /**
   * Arithmetic addition of a duration to a time.
   * @param a_diff - Parameter
   * @returns Result value
   */
  add(a_diff: Iso8601_duration): Iso8601_time {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = time.add(dur);
      const newTime = new Iso8601_time();
      newTime.value = result.toString();
      return newTime;
    } catch (e) {
      throw new Error(`Failed to add duration to time: ${e}`);
    }
  }

  /**
   * Arithmetic subtraction of a duration from a time.
   * @param a_diff - Parameter
   * @returns Result value
   */
  subtract(a_diff: Iso8601_duration): Iso8601_time {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const time = TemporalAPI.PlainTime.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = time.subtract(dur);
      const newTime = new Iso8601_time();
      newTime.value = result.toString();
      return newTime;
    } catch (e) {
      throw new Error(`Failed to subtract duration from time: ${e}`);
    }
  }

  /**
   * Difference of two times.
   * @param a_time - Parameter
   * @returns Result value
   */
  diff(a_time: Iso8601_time): Iso8601_duration {
    const val = this.value || "";
    const otherVal = a_time.value || "";
    try {
      const time1 = TemporalAPI.PlainTime.from(val);
      const time2 = TemporalAPI.PlainTime.from(otherVal);
      const diff = time1.since(time2);
      const duration = new Iso8601_duration();
      duration.value = diff.toString();
      return duration;
    } catch (e) {
      throw new Error(`Failed to calculate time difference: ${e}`);
    }
  }

  /**
   * Compares this time with another for ordering.
   * @param other - The object to compare with
   * @returns true if this time is less than the other
   */
  override less_than(other: Ordered): Boolean {
    if (!(other instanceof Iso8601_time)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const time1 = TemporalAPI.PlainTime.from(val);
      const time2 = TemporalAPI.PlainTime.from(otherVal);
      return new Boolean(TemporalAPI.PlainTime.compare(time1, time2) < 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val < otherVal);
    }
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Iso8601_time)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const time1 = TemporalAPI.PlainTime.from(val);
      const time2 = TemporalAPI.PlainTime.from(otherVal);
      return new Boolean(TemporalAPI.PlainTime.compare(time1, time2) === 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val === otherVal);
    }
  }
}

/**
 * Represents an ISO 8601 date, including partial and extended forms. Value may be:
 *
 * * \`YYYY-MM-DD\` (extended, preferred)
 * * \`YYYYMMDD\` (compact)
 * * a partial invariant.
 *
 * See \`Time_definitions._valid_iso8601_date()_\` for validity.
 */
export class Iso8601_date extends Iso8601_type {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("ISO8601_DATE", Iso8601_date);
  }

  /**
   * Extract the year part of the date as an Integer.
   *
   * Uses Temporal API for robust ISO 8601 date parsing.
   * @returns Result value
   */
  year(): Integer {
    const val = this.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      return Integer.from(date.year);
    } catch {
      // Try to parse just year from partial date (YYYY)
      const match = val.match(/^(\d{4})/);
      if (match) {
        return Integer.from(parseInt(match[1], 10));
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the month part of the date as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 date parsing.
   * @returns Result value
   */
  month(): Integer {
    const val = this.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      return Integer.from(date.month);
    } catch {
      // Try PlainYearMonth for partial dates
      try {
        const ym = TemporalAPI.PlainYearMonth.from(val);
        return Integer.from(ym.month);
      } catch {
        // Return 0 if no month present
      }
    }
    return Integer.from(0);
  }

  /**
   * Extract the day part of the date as an Integer, or return 0 if not present.
   *
   * Uses Temporal API for robust ISO 8601 date parsing.
   * @returns Result value
   */
  day(): Integer {
    const val = this.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      return Integer.from(date.day);
    } catch {
      // Return 0 if parsing fails or no day component
    }
    return Integer.from(0);
  }

  /**
   * Indicates whether month in year is unknown. If so, the date is of the form \`"YYYY"\`.
   * @returns Result value
   */
  month_unknown(): Boolean {
    return new Boolean(this.month().value === 0);
  }

  /**
   * Indicates whether day in month is unknown. If so, and month is known, the date is of the form \`"YYYY-MM"\` or \`"YYYYMM"\`.
   * @returns Result value
   */
  day_unknown(): Boolean {
    return new Boolean(this.day().value === 0);
  }

  /**
   * True if this date is partial, i.e. if days or more is missing.
   * @returns Result value
   */
  is_partial(): Boolean {
    return this.day_unknown();
  }

  /**
   * True if this date uses \`'-'\` separators.
   * @returns Result value
   */
  is_extended(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes("-"));
  }

  /**
   * Return string value in extended format.
   *
   * Uses Temporal API to parse and format in extended ISO 8601 format.
   * @returns Result value
   */
  as_string(): String {
    const val = this.value || "";
    try {
      // Try full date first
      const date = TemporalAPI.PlainDate.from(val);
      return String.from(date.toString());
    } catch {
      try {
        // Try year-month
        const ym = TemporalAPI.PlainYearMonth.from(val);
        return String.from(ym.toString());
      } catch {
        // Return original if parsing fails
        return String.from(val);
      }
    }
  }

  /**
   * Arithmetic addition of a duration to a date.
   * @param a_diff - Parameter
   * @returns Result value
   */
  add(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = date.add(dur);
      const newDate = new Iso8601_date();
      newDate.value = result.toString();
      return newDate;
    } catch (e) {
      throw new Error(`Failed to add duration to date: ${e}`);
    }
  }

  /**
   * Arithmetic subtraction of a duration from a date.
   * @param a_diff - Parameter
   * @returns Result value
   */
  subtract(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      const result = date.subtract(dur);
      const newDate = new Iso8601_date();
      newDate.value = result.toString();
      return newDate;
    } catch (e) {
      throw new Error(`Failed to subtract duration from date: ${e}`);
    }
  }

  /**
   * Difference of two dates.
   * @param a_date - Parameter
   * @returns Result value
   */
  diff(a_date: Iso8601_date): Iso8601_duration {
    const val = this.value || "";
    const otherVal = a_date.value || "";
    try {
      const date1 = TemporalAPI.PlainDate.from(val);
      const date2 = TemporalAPI.PlainDate.from(otherVal);
      const diff = date1.since(date2);
      const duration = new Iso8601_duration();
      duration.value = diff.toString();
      return duration;
    } catch (e) {
      throw new Error(`Failed to calculate date difference: ${e}`);
    }
  }

  /**
   * Addition of nominal duration represented by \`_a_diff_\`. For example, a duration of \`'P1Y'\` means advance to the same date next year, with the exception of the date 29 February in a leap year, to which the addition of a nominal year will result in 28 February of the following year. Similarly, \`'P1M'\` is understood here as a nominal month, the addition of which will result in one of:
   *
   * * the same day in the following month, if it exists, or;
   * * one or two days less where the following month is shorter, or;
   * * in the case of adding a month to the date 31 Jan, the result will be 28 Feb in a non-leap year (i.e. three less) and 29 Feb in a leap year (i.e. two less).
   * @param a_diff - Parameter
   * @returns Result value
   */
  add_nominal(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      // Use Temporal's constrain overflow to get calendar semantics
      const result = date.add(dur, { overflow: "constrain" });
      const newDate = new Iso8601_date();
      newDate.value = result.toString();
      return newDate;
    } catch (e) {
      throw new Error(`Failed to add nominal duration to date: ${e}`);
    }
  }

  /**
   * Subtraction of nominal duration represented by \`_a_diff_\`. See \`_add_nominal_()\` for semantics.
   * @param a_diff - Parameter
   * @returns Result value
   */
  subtract_nominal(a_diff: Iso8601_duration): Iso8601_date {
    const val = this.value || "";
    const diffVal = a_diff.value || "";
    try {
      const date = TemporalAPI.PlainDate.from(val);
      const dur = TemporalAPI.Duration.from(
        Iso8601_duration.normalizeWeeks(diffVal),
      );
      // Use Temporal's constrain overflow to get calendar semantics
      const result = date.subtract(dur, { overflow: "constrain" });
      const newDate = new Iso8601_date();
      newDate.value = result.toString();
      return newDate;
    } catch (e) {
      throw new Error(`Failed to subtract nominal duration from date: ${e}`);
    }
  }

  /**
   * Compares this date with another for ordering.
   * @param other - The object to compare with
   * @returns true if this date is less than the other
   */
  override less_than(other: Ordered): Boolean {
    if (!(other instanceof Iso8601_date)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const date1 = TemporalAPI.PlainDate.from(val);
      const date2 = TemporalAPI.PlainDate.from(otherVal);
      return new Boolean(TemporalAPI.PlainDate.compare(date1, date2) < 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val < otherVal);
    }
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Iso8601_date)) {
      return new Boolean(false);
    }

    const val = this.value || "";
    const otherVal = other.value || "";

    try {
      const date1 = TemporalAPI.PlainDate.from(val);
      const date2 = TemporalAPI.PlainDate.from(otherVal);
      return new Boolean(TemporalAPI.PlainDate.compare(date1, date2) === 0);
    } catch {
      // Fallback to string comparison if parsing fails
      return new Boolean(val === otherVal);
    }
  }
}

/**
 * ISO8601 timezone string, in format:
 *
 * * \`Z | ±hh[mm]\`
 *
 * where:
 *
 * * \`hh\` is "00" - "23" (0-filled to two digits)
 * * \`mm\` is "00" - "59" (0-filled to two digits)
 * * \`Z\` is a literal meaning UTC (modern replacement for GMT), i.e. timezone \`+0000\`
 */
export class Iso8601_timezone extends Iso8601_type {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("ISO8601_TIMEZONE", Iso8601_timezone);
  }

  /**
   * Extract the hour part of timezone, as an Integer in the range `00 - 14`.
   * @returns Result value
   */
  hour(): Integer {
    const val = this.value || "";
    if (val === "Z") return Integer.from(0);
    const match = val.match(/([+-])(\d{2}):?(\d{2})/);
    if (match) {
      return Integer.from(parseInt(match[2], 10));
    }
    return Integer.from(0);
  }

  /**
   * Extract the minute part of timezone, as an Integer, usually either 0 or 30.
   * @returns Result value
   */
  minute(): Integer {
    const val = this.value || "";
    if (val === "Z") return Integer.from(0);
    const match = val.match(/([+-])(\d{2}):?(\d{2})?/);
    if (match && match[3]) {
      return Integer.from(parseInt(match[3], 10));
    }
    return Integer.from(0);
  }

  /**
   * Direction of timezone expresssed as +1 or -1.
   * @returns Result value
   */
  sign(): Integer {
    const val = this.value || "";
    if (val === "Z") return Integer.from(1); // UTC is positive
    const match = val.match(/([+-])/);
    if (match) {
      return Integer.from(match[1] === "+" ? 1 : -1);
    }
    return Integer.from(1);
  }

  /**
   * Indicates whether minute part known.
   * @returns Result value
   */
  minute_unknown(): Boolean {
    const val = this.value || "";
    if (val === "Z") return new Boolean(false); // Z always has minutes (00)
    const match = val.match(/([+-])(\d{2}):?(\d{2})?/);
    // Minutes are unknown if not specified
    return new Boolean(!match || !match[3]);
  }

  /**
   * True if this time zone is partial, i.e. if minutes is missing.
   * @returns Result value
   */
  is_partial(): Boolean {
    return this.minute_unknown();
  }

  /**
   * True if this time-zone uses ‘:’ separators.
   * @returns Result value
   */
  is_extended(): Boolean {
    const val = this.value || "";
    return new Boolean(val.includes(":"));
  }

  /**
   * True if timezone is UTC, i.e. \`+0000\` or \`Z\`.
   * @returns Result value
   */
  is_gmt(): Boolean {
    const val = this.value || "";
    if (val === "Z") return new Boolean(true);
    const match = val.match(/([+-])(\d{2}):?(\d{2})?/);
    if (match) {
      const hours = parseInt(match[2], 10);
      const minutes = match[3] ? parseInt(match[3], 10) : 0;
      return new Boolean(hours === 0 && minutes === 0);
    }
    return new Boolean(false);
  }

  /**
   * Return timezone string in extended format.
   * @returns Result value
   */
  as_string(): String {
    const val = this.value || "";
    if (val === "Z") return String.from("Z");

    const match = val.match(/([+-])(\d{2}):?(\d{2})?/);
    if (match) {
      const sign = match[1];
      const hours = match[2];
      const minutes = match[3] || "00";
      // Return in extended format with colon
      return String.from(`${sign}${hours}:${minutes}`);
    }
    return String.from(val);
  }

  /**
   * Compares this timezone with another for ordering.
   * Timezones are ordered by their offset from UTC in minutes.
   * @param other - The object to compare with
   * @returns true if this timezone is less than the other
   */
  override less_than(other: Ordered): Boolean {
    if (!(other instanceof Iso8601_timezone)) {
      return new Boolean(false);
    }

    // Calculate total offset in minutes for this timezone
    const thisSign = this.sign().value || 1;
    const thisHour = this.hour().value || 0;
    const thisMinute = this.minute().value || 0;
    const thisOffset = thisSign * (thisHour * 60 + thisMinute);

    // Calculate total offset in minutes for other timezone
    const otherSign = other.sign().value || 1;
    const otherHour = other.hour().value || 0;
    const otherMinute = other.minute().value || 0;
    const otherOffset = otherSign * (otherHour * 60 + otherMinute);

    return new Boolean(thisOffset < otherOffset);
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Iso8601_timezone)) {
      return new Boolean(false);
    }

    // Compare by offset in minutes
    const thisSign = this.sign().value || 1;
    const thisHour = this.hour().value || 0;
    const thisMinute = this.minute().value || 0;
    const thisOffset = thisSign * (thisHour * 60 + thisMinute);

    const otherSign = other.sign().value || 1;
    const otherHour = other.hour().value || 0;
    const otherMinute = other.minute().value || 0;
    const otherOffset = otherSign * (otherHour * 60 + otherMinute);

    return new Boolean(thisOffset === otherOffset);
  }
}
